import {
	ContactSource,
	getWorkspaceContactIdFromUserId
} from "client/Api/Email/contacts";
import {
	getUserProfileImageUrl,
	UserIdentityModel,
	UserStatus
} from "client/Api/UserProfile/service";
import {
	ListItem,
	PersonAvatar,
	PersonAvatarStatusValue,
	PipeSeparator,
	Space
} from "client/Components";
import { getComputedDisplayName } from "client/Features/AddressBook/common";
import { useContactDialog } from "client/Features/AddressBook/ContactDialog";
import { isNotNil, isNotNilOrWhiteSpace } from "client/Misc/utils";
import { localize } from "client_legacy/ndaw/culture";
import {
	Fragment,
	ReactElement,
	useCallback
} from "react";

export type UserListItemProps = {
	user: UserIdentityModel;
	avatarStatus?: PersonAvatarStatusValue;
	trackingId: string;
	noMobileBorderBottom?: boolean;
};

export const UserListItem = (props: UserListItemProps): ReactElement => {
	const {
		user,
		avatarStatus,
		trackingId,
		noMobileBorderBottom = false
	} = props;

	const [contactDialog, openContactDialog] = useContactDialog();

	const {
		userId,
		avatarId,
		jobTitle,
		department,
		officeLocation,
		status
	} = user;

	const clickHandler = useCallback(
		(): void => {
			openContactDialog({
				source: ContactSource.Workspace,
				initialView: {
					kind: "details",
					contactId: getWorkspaceContactIdFromUserId(userId)
				}
			});
		},
		[userId, openContactDialog]
	);

	const isDeletedUser: boolean = status === UserStatus.Deleted;

	const avatarUrl: string | null = isNotNil(avatarId)
		? getUserProfileImageUrl(avatarId)
		: null;

	const {
		computedDisplayName,
		initials
	} = getComputedDisplayName(
		!isDeletedUser
			? user
			: {
				displayName: localize("_DeletedUser")
			}
	);

	const jobInfo: readonly string[] = [
		jobTitle,
		department,
		officeLocation
	]
		.filter(isNotNilOrWhiteSpace);

	return (
		<>
			<ListItem
				trackingId={trackingId}
				noMobileBorderBottom={noMobileBorderBottom}
				interactionMode={
					status === UserStatus.Active
						? "default"
						: "none"
				}
			>
				<ListItem.Content
					thumbnail={
						!isDeletedUser
							? (
								<PersonAvatar
									trackingId="avatar"
									initials={initials}
									imageSrc={avatarUrl}
									status={avatarStatus}
									size="small"
								/>
							)
							: (
								<PersonAvatar
									trackingId="avatar"
									initials={initials}
									size="small"
								/>
							)
					}
					onClick={
						status === UserStatus.Active
							? clickHandler
							: undefined
					}
				>
					<ListItem.Title
						trackingId="title"
					>
						{computedDisplayName}
						{status === UserStatus.Inactive && (
							<>
								<Space />
								({localize("_InactiveUser")})
							</>
						)}
					</ListItem.Title>
					<ListItem.SubTitle>
						{jobInfo.map(
							(x, index) => (
								<Fragment key={index}>
									{index > 0 && <PipeSeparator/>}
									{x}
								</Fragment>
							)
						)}
					</ListItem.SubTitle>
				</ListItem.Content>
			</ListItem>
			{contactDialog}
		</>
	);
};
UserListItem.displayName = "UserListItem";